import * as React from 'react';

import { evidentlyIsMockedOut } from '../helpers';
import { evidentlyProvider } from '../providers';
import { FeatureOverrideViewModel } from '../types';

import { LoadingIcon, TickIcon, CleanUpIcon } from './icons';

import * as styles from './widget.less';

type WidgetState =
	| { type: 'hidden'; features?: FeatureOverrideViewModel[] }
	| { type: 'loading' }
	| { type: 'visible'; features: FeatureOverrideViewModel[] };

const OverrideOption: React.FC<{
	id: string;
	name: string;
	label: string;
	value: string | number | boolean;
	checked: boolean;
	onChange: () => void;
}> = React.memo(
	({ id, name, label, value, checked, onChange }) => {
		return (
			<div key={name} className={styles.override}>
				<input id={id} type="radio" value={value.toString()} checked={checked} onChange={onChange} />
				<label htmlFor={id} className={styles.overrideLabel}>
					{label}
				</label>
			</div>
		);
	},
	(prev, next) => prev.checked === next.checked
);

export const EvidentlyOverridesWidgetPanel: React.FC<{
	toggleWidget: () => void;
	widgetState: WidgetState;
	onReset: () => void;
	onOptionChanged: (onOptionSelected: () => Promise<FeatureOverrideViewModel[]>) => () => void;
}> = ({ toggleWidget, widgetState, onReset, onOptionChanged }) => {
	const [clearedTimeout, setClearedTimeout] = React.useState<number | undefined>(undefined);

	return (
		<div className={styles.container}>
			<div className={styles.heading} role="button" tabIndex={0} onClick={toggleWidget}>
				<strong className={styles.headingText}>A/B Testing Feature Overrides</strong>
				{widgetState.type === 'loading' ? (
					<LoadingIcon />
				) : (
					<button
						type="button"
						className={styles.copy}
						title={clearedTimeout ? 'Caches cleared!' : 'Reset and clear caches'}
						onClick={async (ev) => {
							ev.stopPropagation();
							onReset();
							if (clearedTimeout) {
								window.clearTimeout(clearedTimeout);
							}
							setClearedTimeout(window.setTimeout(() => setClearedTimeout(undefined), 2000));
						}}
					>
						{clearedTimeout ? <TickIcon /> : <CleanUpIcon />}
					</button>
				)}
			</div>
			{widgetState.type === 'visible' &&
				(widgetState.features.length ? (
					<div className={styles.features}>
						{widgetState.features.map(({ featureName, options }) => (
							<div key={featureName} className={styles.feature}>
								<div className={styles.featureHeading}>{featureName}</div>
								{options.map(({ name, value, selected, onSelected }) => (
									<OverrideOption
										key={name}
										id={`${featureName}- ${name}`}
										name={featureName}
										label={name}
										value={value}
										checked={selected}
										onChange={onOptionChanged(onSelected)}
									/>
								))}
							</div>
						))}
					</div>
				) : (
					<div>No features are being A/B tested</div>
				))}
		</div>
	);
};

export const EvidentlyOverridesWidget: React.FC = () => {
	const [widgetState, setWidgetState] = React.useState<WidgetState>({ type: 'hidden' });

	const toggleWidget = React.useCallback(async () => {
		if (widgetState.type === 'loading') {
			return;
		}

		if (widgetState.type === 'visible') {
			setWidgetState((current) => ({ ...current, type: 'hidden' }));
			return;
		}

		if (widgetState.features) {
			setWidgetState((current) => ({ ...(current as any), type: 'visible' }));
			return;
		}

		try {
			setWidgetState({ type: 'loading' });
			const features = await evidentlyProvider.getFeatureOverridesViewModel();
			setWidgetState({ type: 'visible', features });
		} catch {
			setWidgetState({ type: 'visible', features: [] });
		}
	}, [widgetState]);

	if (evidentlyIsMockedOut()) {
		return null;
	}

	return (
		<div className={styles.wrapper}>
			<EvidentlyOverridesWidgetPanel
				toggleWidget={toggleWidget}
				widgetState={widgetState}
				onReset={async () => {
					const features = (await evidentlyProvider.reset()) as FeatureOverrideViewModel[];
					setWidgetState((currentState) => ({ ...currentState, features }));
				}}
				onOptionChanged={(onOptionSelected: () => Promise<FeatureOverrideViewModel[]>) => async () => {
					const features = await onOptionSelected();
					setWidgetState((currentState) => ({ ...currentState, features }));
				}}
			/>
		</div>
	);
};
